import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useEffect } from 'react';
import { FieldErrors, FormProvider, useWatch } from 'react-hook-form';
import {
  Button,
  DatePicker,
  FormActions,
  FormLayout,
  FormRow,
  FormSection,
  TextArea,
  TextInput,
  Toggle,
} from '~/components';
import { useCustomForm } from '~/hooks/useCustomForm';
import { useServerWarnings } from '~/hooks/useServerWarnings';
import { logger } from '~/utils/logger';
import { DEA_LICENSE_NOTES_MAX, DeaLicenseFormSchema, DeaLicenseFormValues } from './types';

const DeaFormLogger = logger.child('DeaForm');
const TAGS = CT.account.licenses.dea.form;

interface Props {
  onCancel: () => void;
  sendLicenseUpdate: (values: DeaLicenseFormValues) => void;
  sendLicenseCreate: (values: DeaLicenseFormValues) => void;
  deaLicenseForm: DeaLicenseFormValues;
  modal: {
    edit: boolean;
    create: boolean;
    type: 'dea' | 'cds';
  };
}

const VALID_FIELDS = [
  'deaNum',
  'stateCode',
  'schedule',
  'expiryDate',
  'issueDate',
  'licenseNotes',
  'activeFlag',
  'restrictedUseFlag',
] as (keyof DeaLicenseFormValues)[];

export const DeaLicenseForm = (props: Props) => {
  const { deaLicenseForm, modal, sendLicenseUpdate, sendLicenseCreate, onCancel } = props;
  const methods = useCustomForm<DeaLicenseFormValues>({
    resolver: zodResolver(DeaLicenseFormSchema),
    defaultValues: deaLicenseForm,
  });
  const { control, formState, trigger, handleSubmit } = methods;

  useServerWarnings(methods, deaLicenseForm.warnings, VALID_FIELDS);

  const watchedActiveFlag = useWatch({ control, name: 'activeFlag' });
  const watchedIssueDate = useWatch({ control, name: 'issueDate' });
  useEffect(() => {
    // [MCV-1428] Only revalidate expiryDate once the user has interacted with it.
    // Otherwise a fresh create-form would flag "Invalid date" the moment the user
    // typed into issueDate, before they had a chance to fill in expiryDate.
    if (
      formState.touchedFields.expiryDate &&
      (formState.dirtyFields.activeFlag || formState.dirtyFields.issueDate)
    ) {
      void trigger('expiryDate');
    }
  }, [
    formState.dirtyFields.activeFlag,
    formState.dirtyFields.issueDate,
    formState.touchedFields.expiryDate,
    trigger,
    watchedActiveFlag,
    watchedIssueDate,
  ]);

  const onSubmitSuccess = async (values: DeaLicenseFormValues) => {
    if (modal.edit) {
      sendLicenseUpdate(values);
    } else {
      sendLicenseCreate(values);
    }
  };

  const onSubmitError = (errors: FieldErrors<DeaLicenseFormValues>) => {
    DeaFormLogger.error('Errors:', errors);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>
        <FormLayout className="text-content-primary">
          <FormSection componentsPerLine={1}>
            <FormRow>
              <TextInput
                name={'deaNum'}
                cyTag={TAGS.deaNum}
                label={'DEA Number*'}
                description={
                  'Format: Two uppercase letters followed by seven digits (e.g. AB1234567)'
                }
              />
            </FormRow>
            <FormRow>
              <TextInput
                name={'stateCode'}
                label={'Issuing State'}
                maxLength={2}
                description={'Two-letter state or jurisdiction code'}
              />
            </FormRow>
            <FormRow>
              <TextInput
                name={'schedule'}
                label={'Authorized Schedule'}
                maxLength={32}
                description={'For example: II-V'}
              />
            </FormRow>
            <FormRow>
              <TextArea
                name={'licenseNotes'}
                cyTag={TAGS.licenseNotes}
                label={'License Notes'}
                maxLength={DEA_LICENSE_NOTES_MAX}
              />
            </FormRow>
          </FormSection>
          <FormSection componentsPerLine={2}>
            <FormRow>
              <DatePicker label={'Issue Date*'} cyTag={TAGS.issueDate} name={'issueDate'} />
            </FormRow>
            <FormRow>
              <DatePicker label={'Expiry Date*'} cyTag={TAGS.expiryDate} name={'expiryDate'} />
            </FormRow>
          </FormSection>
          <FormSection componentsPerLine={2} className="text-black">
            <FormRow>
              <Toggle<DeaLicenseFormValues>
                cyTag={TAGS.restrictedUseFlag}
                description={'Indicates if this license is for restricted use only'}
                label={'Restricted Use'}
                name={'restrictedUseFlag'}
              />
            </FormRow>
            <FormRow>
              <Toggle<DeaLicenseFormValues>
                cyTag={TAGS.activeFlag}
                description={'Indicates if this license is currently active'}
                label={'Active Status'}
                name={'activeFlag'}
              />
            </FormRow>
          </FormSection>
          <FormActions>
            <Button
              cyTag={TAGS.cancelBtn}
              type="button"
              label={'Cancel'}
              onClick={onCancel}
              colorVariant="secondary"
            />
            <Button
              cyTag={TAGS.submitBtn}
              type="submit"
              label={modal.edit ? 'Update License' : 'Add License'}
            />
          </FormActions>
        </FormLayout>
      </form>
    </FormProvider>
  );
};
